import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, ListChecks, Calendar, MapPin, User, UploadSimple, FileText, Image as ImageIcon, ArrowSquareOut, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { COMMITMENT_TIPOS, COMMITMENT_ESTADOS, MEETING_TIPOS } from "../lib/constants";
import { formatDate, formatTime, formatLabel } from "../lib/format";
import RichEditor, { RichView } from "../components/RichEditor";
import { TabLabel } from "../components/DetailHelpers";
import { useConfirm } from "../components/ConfirmDialog";

export default function MeetingDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/meetings/${id}`);
    setData(data);
    setForm({
      ...data.meeting,
      hora: data.meeting.hora ? String(data.meeting.hora).slice(0, 5) : "",
      asistentes_internos: data.meeting.asistentes_internos || [],
      asistentes_externos: data.meeting.asistentes_externos || [],
      temas_tratados: data.meeting.temas_tratados || [],
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data || !form) return <div className="p-8 text-slate-500">Cargando…</div>;

  const save = async () => {
    try {
      const payload = {
        asunto: form.asunto,
        fecha: form.fecha,
        hora: form.hora || null,
        lugar: form.lugar,
        tipo: form.tipo,
        related_entity_type: form.related_entity_type,
        related_entity_id: form.related_entity_id,
        asistentes_internos: form.asistentes_internos,
        asistentes_externos: form.asistentes_externos,
        temas_tratados: form.temas_tratados,
        registro_detallado: form.registro_detallado,
        acuerdos: form.acuerdos,
        minuta_url: form.minuta_url,
        responsable_seguimiento: form.responsable_seguimiento,
        estado_seguimiento: form.estado_seguimiento,
      };
      await api.put(`/meetings/${id}`, payload);
      toast.success("Cambios guardados");
      setEditing(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  const removeMeeting = async () => {
    if (!(await confirm({ title: "Eliminar reunión", description: "¿Deseas eliminar esta reunión completa? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/meetings/${id}`);
    nav("/reuniones");
  };

  const m = data.meeting;
  const photoDocs = (data.documents || []).filter(d => (d.content_type || "").startsWith("image/"));
  const fileDocs = (data.documents || []).filter(d => !(d.content_type || "").startsWith("image/"));

  return (
    <div data-testid="meeting-detail-page">
      <PageHeader
        title={m.asunto}
        description={`${formatDate(m.fecha)}${m.hora ? ` · ${formatTime(m.hora)}` : ""}${m.lugar ? ` · ${m.lugar}` : ""}${m.tipo ? ` · ${m.tipo}` : ""}`}
        actions={
          <div className="flex gap-2">
            <Link to="/reuniones"><Button variant="outline">Volver</Button></Link>
            {!editing && (
              <Button onClick={() => setEditing(true)} className="bg-slate-900 text-white" data-testid="edit-meeting-btn">
                <PencilSimple /> Editar
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); load(); }}><X /> Cancelar</Button>
                <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-meeting-btn"><FloppyDisk /> Guardar</Button>
              </>
            )}
            <Button variant="ghost" onClick={removeMeeting} className="text-red-600"><Trash /></Button>
          </div>
        }
      />

      <div className="px-6 py-6 space-y-4">
        {/* Summary band */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Mini icon={Calendar} label="Fecha" value={`${formatDate(m.fecha)}${m.hora ? ` ${formatTime(m.hora)}` : ""}`} />
          <Mini icon={MapPin} label="Lugar" value={m.lugar || "—"} />
          <Mini icon={ListChecks} label="Compromisos" value={data.commitments.length} />
          <Mini icon={User} label="Asistentes" value={(m.asistentes_internos?.length || 0) + (m.asistentes_externos?.length || 0)} />
          <Mini icon={FileText} label="Evidencias" value={data.documents.length} />
        </div>

        {data.related && (
          <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-slate-500">Vinculada a</span>
            <StatusBadge tone="info">{data.related.type === "community" ? "Comunidad" : "Autoridad"}</StatusBadge>
            <Link to={(() => {
                    if (data.related.type === "community") return `/comunidades/${data.related.id}`;
                    const etype = data.related.entity_type;
                    const route = etype === "gobierno" ? "autoridades-gobierno"
                                : etype === "parlamentario" ? "parlamentarios"
                                : etype === "gore" ? "autoridades-gore"
                                : etype === "organizacion" ? "organizaciones"
                                : "autoridades-comunales";
                    return `/${route}/${data.related.id}`;
                  })()}
                  className="font-medium hover:underline flex items-center gap-1">
              {data.related.name} <ArrowSquareOut size={12} />
            </Link>
          </div>
        )}

        <Tabs defaultValue="resumen">
          <TabsList>
            <TabsTrigger value="resumen"><TabLabel label="Resumen" hasData /></TabsTrigger>
            <TabsTrigger value="participantes"><TabLabel label="Participantes" hasData={(form.asistentes_internos?.length || 0) + (form.asistentes_externos?.length || 0) > 0} /></TabsTrigger>
            <TabsTrigger value="agenda"><TabLabel label="Agenda y Acuerdos" hasData={(form.temas_tratados?.length || 0) > 0 || !!form.acuerdos || !!form.registro_detallado} /></TabsTrigger>
            <TabsTrigger value="compromisos" data-testid="tab-compromisos"><TabLabel label={`Compromisos (${data.commitments.length})`} hasData={data.commitments.length > 0} /></TabsTrigger>
            <TabsTrigger value="evidencias"><TabLabel label="Evidencias" hasData={(data.documents?.length || 0) > 0} /></TabsTrigger>
          </TabsList>

          {/* RESUMEN */}
          <TabsContent value="resumen" className="mt-4">
            <Card className="rounded-md">
              <CardHeader><CardTitle>Información general</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <FormField label="Asunto" editing={editing} value={form.asunto} onChange={(v) => setForm({ ...form, asunto: v })} colSpan={3} />
                <FormField label="Fecha" type="date" editing={editing} value={form.fecha} onChange={(v) => setForm({ ...form, fecha: v })} />
                <FormField label="Hora" type="time" editing={editing} value={form.hora} onChange={(v) => setForm({ ...form, hora: v })} />
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Tipo</Label>
                  {editing ? (
                    <Select value={form.tipo || ""} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona tipo" /></SelectTrigger>
                      <SelectContent>{MEETING_TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-1 text-sm text-slate-800">{form.tipo || <span className="text-slate-400">—</span>}</div>
                  )}
                </div>
                <FormField label="Lugar" editing={editing} value={form.lugar} onChange={(v) => setForm({ ...form, lugar: v })} colSpan={2} />
                <FormField label="Responsable seguimiento" editing={editing} value={form.responsable_seguimiento} onChange={(v) => setForm({ ...form, responsable_seguimiento: v })} />
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Estado</Label>
                  {editing ? (
                    <Select value={form.estado_seguimiento} onValueChange={(v) => setForm({ ...form, estado_seguimiento: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="abierto">Abierto</SelectItem>
                        <SelectItem value="en_seguimiento">En seguimiento</SelectItem>
                        <SelectItem value="cerrado">Cerrado</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : <div className="mt-1"><StatusBadge tone="info">{form.estado_seguimiento}</StatusBadge></div>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARTICIPANTES */}
          <TabsContent value="participantes" className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AsistenteList
              title="Asistentes internos"
              data={form.asistentes_internos}
              editable={editing}
              onChange={(list) => setForm({ ...form, asistentes_internos: list })}
              kind="interno"
            />
            <AsistenteList
              title="Asistentes externos"
              data={form.asistentes_externos}
              editable={editing}
              onChange={(list) => setForm({ ...form, asistentes_externos: list })}
              kind="externo"
            />
            {!editing && (
              <div className="lg:col-span-2 text-xs text-slate-500">Haz click en <strong>Editar</strong> para agregar o quitar asistentes.</div>
            )}
          </TabsContent>

          {/* AGENDA */}
          <TabsContent value="agenda" className="mt-4 space-y-4">
            <TemasTratados
              data={form.temas_tratados}
              editable={editing}
              onChange={(list) => setForm({ ...form, temas_tratados: list })}
            />
            <Card className="rounded-md">
              <CardHeader><CardTitle className="text-base">Acuerdos</CardTitle></CardHeader>
              <CardContent>
                {editing ? (
                  <RichEditor
                    value={form.acuerdos || ""}
                    onChange={(html) => setForm({ ...form, acuerdos: html })}
                    placeholder="Acuerdos formales adoptados en la reunión…"
                    height={240}
                  />
                ) : (
                  <RichView html={form.acuerdos} empty="Sin acuerdos registrados." />
                )}
              </CardContent>
            </Card>
            <Card className="rounded-md">
              <CardHeader>
                <CardTitle className="text-base">Minuta — Registro detallado</CardTitle>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <RichEditor
                    value={form.registro_detallado || ""}
                    onChange={(html) => setForm({ ...form, registro_detallado: html })}
                    placeholder="Bitácora libre, observaciones, contexto, citas relevantes…"
                    height={360}
                  />
                ) : (
                  <RichView html={form.registro_detallado} empty="Sin registro detallado." />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPROMISOS */}
          <TabsContent value="compromisos" className="mt-4">
            <CommitmentsSection
              meetingId={id}
              commitments={data.commitments}
              onChange={load}
            />
          </TabsContent>

          {/* EVIDENCIAS */}
          <TabsContent value="evidencias" className="mt-4 space-y-4">
            <EvidenciasSection
              meetingId={id}
              photos={photoDocs}
              onChange={load}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ============================================================ */

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5"><Icon size={12} /> {label}</div>
      <div className="mt-1 font-display text-lg font-semibold truncate" title={value}>{value}</div>
    </div>
  );
}

function FormField({ label, value, onChange, editing, type = "text", placeholder, colSpan }) {
  return (
    <div className={colSpan ? `col-span-${colSpan}` : ""} style={colSpan ? { gridColumn: `span ${colSpan} / span ${colSpan}` } : {}}>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {editing ? (
        <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <div className="mt-1 text-sm text-slate-800">{value || <span className="text-slate-400">—</span>}</div>
      )}
    </div>
  );
}

/* ---- Asistentes ---- */
function AsistenteList({ title, data, editable, onChange, kind }) {
  const [name, setName] = useState("");
  const [cargo, setCargo] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [email, setEmail] = useState("");

  const add = () => {
    if (!name.trim()) return;
    onChange([...(data || []), { name, cargo, empresa, email }]);
    setName(""); setCargo(""); setEmpresa(""); setEmail("");
  };
  const remove = (idx) => {
    const next = [...data]; next.splice(idx, 1); onChange(next);
  };

  return (
    <Card className="rounded-md">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>{title}</span>
          <span className="text-xs font-normal text-slate-500">{data?.length || 0}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {(!data || data.length === 0) && (
          <div className="px-4 py-6 text-sm text-slate-500">Sin {title.toLowerCase()}.</div>
        )}
        {data && data.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {data.map((a, idx) => (
              <li key={idx} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0 flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"><User size={14} /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <div className="text-xs text-slate-500 truncate">{[a.cargo, a.empresa, a.email].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                </div>
                {editable && <Button size="sm" variant="ghost" onClick={() => remove(idx)}><Trash size={14} className="text-red-600" /></Button>}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 grid grid-cols-2 gap-2">
            <Input placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} data-testid={`asistente-${kind}-name`} />
            <Input placeholder="Cargo" value={cargo} onChange={(e) => setCargo(e.target.value)} />
            <Input placeholder="Empresa / Organización" value={empresa} onChange={(e) => setEmpresa(e.target.value)} />
            <Input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <div className="col-span-2">
              <Button onClick={add} size="sm" className="bg-slate-900 text-white w-full" data-testid={`add-asistente-${kind}`}><Plus /> Agregar</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---- Temas tratados ---- */
function TemasTratados({ data, editable, onChange }) {
  const [tema, setTema] = useState("");
  const [notas, setNotas] = useState("");

  const add = () => {
    if (!tema.trim()) return;
    onChange([...(data || []), { tema, notas }]);
    setTema(""); setNotas("");
  };
  const remove = (idx) => {
    const next = [...data]; next.splice(idx, 1); onChange(next);
  };

  return (
    <Card className="rounded-md">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Temas tratados</span>
          <span className="text-xs font-normal text-slate-500">{data?.length || 0}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {(!data || data.length === 0) && <div className="text-sm text-slate-500">Aún no se registran temas tratados.</div>}
        {data && data.length > 0 && (
          <ol className="space-y-3">
            {data.map((t, idx) => {
              // Resilient: support both legacy ["string"] format and {tema, notas} objects.
              const tema = typeof t === "string" ? t : (t?.tema || "");
              const notas = typeof t === "string" ? "" : (t?.notas || "");
              return (
                <li key={idx} className="flex gap-3 border-l-2 border-slate-200 pl-3">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{idx + 1}. {tema}</div>
                    {notas && <div className="text-xs text-slate-600 whitespace-pre-wrap mt-0.5">{notas}</div>}
                  </div>
                  {editable && <Button size="sm" variant="ghost" onClick={() => remove(idx)}><Trash size={14} className="text-red-600" /></Button>}
                </li>
              );
            })}
          </ol>
        )}
        {editable && (
          <div className="mt-4 space-y-2 rounded-md border border-dashed border-slate-300 bg-slate-50/60 p-3">
            <Input placeholder="Tema tratado" value={tema} onChange={(e) => setTema(e.target.value)} data-testid="tema-input" />
            <Textarea rows={2} placeholder="Notas / detalle (opcional)" value={notas} onChange={(e) => setNotas(e.target.value)} />
            <Button onClick={add} size="sm" className="bg-slate-900 text-white" data-testid="add-tema-btn"><Plus /> Agregar tema</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---- Compromisos generados ---- */
function CommitmentsSection({ meetingId, commitments, onChange }) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", tipo: "comunitario", responsable_interno: "",
    fecha_compromiso: "", estado: "pendiente", inversion: 0, beneficiarios_directos: 0,
  });

  const create = async () => {
    try {
      await api.post("/commitments", {
        ...form,
        related_entity_type: "meeting",
        related_entity_id: meetingId,
        fecha_compromiso: form.fecha_compromiso || null,
        inversion: +form.inversion || 0,
        beneficiarios_directos: +form.beneficiarios_directos || 0,
      });
      toast.success("Compromiso creado y vinculado a la reunión");
      setOpen(false);
      setForm({ name: "", description: "", tipo: "comunitario", responsable_interno: "", fecha_compromiso: "", estado: "pendiente", inversion: 0, beneficiarios_directos: 0 });
      onChange();
    } catch (e) { toast.error("Error al crear compromiso"); }
  };

  const removeOne = async (cid) => {
    if (!(await confirm({ title: "Eliminar compromiso", description: "¿Deseas eliminar este compromiso?", danger: true }))) return;
    await api.delete(`/commitments/${cid}`);
    onChange();
  };

  return (
    <Card className="rounded-md">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><ListChecks size={16} /> Compromisos generados ({commitments.length})</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-slate-900 text-white" data-testid="add-meeting-commit-btn"><Plus /> Nuevo compromiso</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nuevo compromiso desde la reunión</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="meeting-commit-name" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMITMENT_TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMITMENT_ESTADOS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Responsable interno</Label>
                <Input value={form.responsable_interno} onChange={(e) => setForm({ ...form, responsable_interno: e.target.value })} />
              </div>
              <div>
                <Label>Fecha compromiso</Label>
                <Input type="date" value={form.fecha_compromiso} onChange={(e) => setForm({ ...form, fecha_compromiso: e.target.value })} />
              </div>
              <div>
                <Label>Inversión (CLP)</Label>
                <Input type="number" value={form.inversion} onChange={(e) => setForm({ ...form, inversion: e.target.value })} />
              </div>
              <div>
                <Label>Beneficiarios directos</Label>
                <Input type="number" value={form.beneficiarios_directos} onChange={(e) => setForm({ ...form, beneficiarios_directos: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={create} className="bg-slate-900 text-white" data-testid="save-meeting-commit-btn">Crear y vincular</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        {commitments.length === 0 ? (
          <div className="px-4 py-8 text-sm text-slate-500 text-center">
            Aún no hay compromisos vinculados a esta reunión.<br />
            <span className="text-xs">Los compromisos creados aquí aparecerán también en la sección <Link to="/compromisos" className="underline">Compromisos</Link>.</span>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Compromiso</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Semáforo</TableHead>
                <TableHead className="text-right">—</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commitments.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell><StatusBadge tone="info">{c.tipo}</StatusBadge></TableCell>
                  <TableCell>{c.responsable_interno || "—"}</TableCell>
                  <TableCell>{formatDate(c.fecha_compromiso)}</TableCell>
                  <TableCell><EstadoBadge value={c.estado} /></TableCell>
                  <TableCell><Semaforo value={c.semaforo} /></TableCell>
                  <TableCell className="text-right">
                    <Link to="/compromisos"><Button size="sm" variant="ghost" title="Ver en Compromisos"><ArrowSquareOut size={14} /></Button></Link>
                    <Button size="sm" variant="ghost" onClick={() => removeOne(c.id)}><Trash size={14} className="text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/* ---- Evidencias ---- */
function EvidenciasSection({ meetingId, photos, onChange }) {
  const confirm = useConfirm();
  const photoRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingTitle, setPendingTitle] = useState("");
  const [preview, setPreview] = useState(null);    // lightbox doc
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");

  const onPickFile = (file) => {
    if (!file) return;
    setPendingFile(file);
    setPendingTitle(file.name.replace(/\.[^.]+$/, ""));
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("related_entity_type", "meeting");
      fd.append("related_entity_id", meetingId);
      if (pendingTitle.trim()) fd.append("description", pendingTitle.trim());
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto subida");
      setPendingFile(null);
      setPendingTitle("");
      onChange();
    } catch (e) { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const cancelUpload = () => { setPendingFile(null); setPendingTitle(""); };

  const remove = async (fid) => {
    if (!(await confirm({ title: "Eliminar foto", description: "¿Deseas eliminar esta foto? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/files/${fid}`);
    if (preview?.id === fid) setPreview(null);
    onChange();
  };

  const startEditTitle = (p) => { setEditingId(p.id); setEditingTitle(p.description || ""); };
  const saveTitle = async (fid) => {
    try {
      await api.patch(`/files/${fid}`, { description: editingTitle.trim() });
      toast.success("Título actualizado");
      setEditingId(null);
      onChange();
    } catch (e) { toast.error("No se pudo guardar el título"); }
  };

  const downloadUrl = (fid) => {
    const token = localStorage.getItem("sigsaop_token");
    return `${API}/files/${fid}/download?auth=${encodeURIComponent(token)}`;
  };

  return (
    <>
      <Card className="rounded-md">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><ImageIcon size={16} /> Fotografías ({photos.length})</CardTitle>
          <div>
            <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => { onPickFile(e.target.files[0]); e.target.value = ""; }} />
            <Button size="sm" className="bg-slate-900 text-white" onClick={() => photoRef.current?.click()} disabled={uploading || !!pendingFile} data-testid="upload-photo-btn">
              <UploadSimple /> Subir foto
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingFile && (
            <div className="rounded-md border border-dashed border-brand-green-300 bg-brand-green-50/50 p-3 flex items-start gap-3" data-testid="pending-upload-row">
              <img src={URL.createObjectURL(pendingFile)} alt="preview" className="h-16 w-16 object-cover rounded border" />
              <div className="flex-1 space-y-2">
                <Label className="text-xs font-medium text-slate-700">Título / descripción de la foto</Label>
                <Input
                  autoFocus
                  value={pendingTitle}
                  onChange={(e) => setPendingTitle(e.target.value)}
                  placeholder="Ej: Minuta firmada, foto de asistentes, fachada del salón…"
                  data-testid="pending-upload-title"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-slate-900 text-white" onClick={confirmUpload} disabled={uploading || !pendingTitle.trim()} data-testid="confirm-upload-btn">
                    {uploading ? "Subiendo…" : "Subir"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelUpload} disabled={uploading}>Cancelar</Button>
                </div>
              </div>
            </div>
          )}

          {photos.length === 0 && !pendingFile ? (
            <div className="text-sm text-slate-500 py-4">Sin fotografías.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {photos.map(p => (
                <div key={p.id} className="group rounded-md overflow-hidden border border-slate-200 bg-white hover:shadow-md transition" data-testid={`photo-${p.id}`}>
                  <button
                    type="button"
                    onClick={() => setPreview(p)}
                    className="block w-full aspect-square bg-slate-50 overflow-hidden"
                    data-testid={`open-photo-${p.id}`}
                  >
                    <img src={downloadUrl(p.id)} alt={p.description || p.original_filename} className="h-full w-full object-cover transition group-hover:scale-105" />
                  </button>
                  <div className="px-2 py-1.5 flex items-center gap-1">
                    <span className="flex-1 text-[11px] text-slate-700 truncate" title={p.description || p.original_filename}>
                      {p.description || p.original_filename}
                    </span>
                    <button type="button" onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700" data-testid={`del-photo-${p.id}`}>
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* LIGHTBOX modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="photo-lightbox"
        >
          <div
            className="max-w-5xl w-full max-h-[90vh] flex flex-col items-stretch gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between text-white">
              {editingId === preview.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    autoFocus
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    className="bg-white text-slate-900 max-w-md"
                  />
                  <Button size="sm" className="bg-brand-green-600" onClick={() => saveTitle(preview.id)}>Guardar</Button>
                  <Button size="sm" variant="ghost" className="text-white" onClick={() => setEditingId(null)}>Cancelar</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-1">
                  <h3 className="font-medium text-base truncate" title={preview.description || preview.original_filename}>
                    {preview.description || preview.original_filename}
                  </h3>
                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => startEditTitle(preview)} title="Editar título">
                    ✎
                  </Button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="text-white hover:bg-white/10 rounded p-2 ml-2"
                aria-label="Cerrar"
                data-testid="close-lightbox"
              >✕</button>
            </div>
            <img
              src={downloadUrl(preview.id)}
              alt={preview.description || preview.original_filename}
              className="max-h-[80vh] w-full object-contain rounded shadow-lg bg-black"
            />
          </div>
        </div>
      )}
    </>
  );
}
